import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Button,
	Container,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Theme,
	Tooltip,
	Typography,
	useMediaQuery,
	Paper,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { BookmarkColorPopover } from '../components/BookmarkColorPopover';
import { entriesUpdateMany, selectAllRepositories, selectAllRepositoryEntries } from './contentSlice';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
	RepositoryListItem,
	useLoadRepositoriesQuery,
	useUpdateRepositoryMutation,
} from '../services/repositoryEntries';
import { api } from '../services/api';
import useAuth from '../components/Auth';
import { isAddRepositoryDialogOpenSetState } from './formDataSlice';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EntryMenu, {
	RowMenuItem,
	RowMenuOption,
	RowMenuOptionEffect,
	RowMenuOptionReorder,
} from '../components/EntryMenu';

function CollectionsView() {
	const { userProfile } = useAuth();
	const { data: remoteRepositories = [], isSuccess: isRepositoriesSuccess } = useLoadRepositoriesQuery(undefined, { skip: !userProfile });
	const localRepositories = useAppSelector(selectAllRepositories);
	const localRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const [updateRepositoryMutation] = useUpdateRepositoryMutation();
	const [bookmarkColorAnchor, setBookmarkColorAnchor] = useState<HTMLElement | null>(null);
	const [bookmarkColorRepository, setBookmarkColorRepository] = useState<RepositoryListItem | null>(null);

	const dispatch = useAppDispatch();
	const loadReppositoriesLocal = useCallback(() => {
		const entryCountsByRepositoryId = localRepositoryEntries.reduce((acc, entry) => {
			acc[entry.repositoryId] = (acc[entry.repositoryId] || 0) + 1;
			return acc;
		}, {} as Record<string, number>);
		return localRepositories.map((repository) => ({
			...repository,
			entryCount: entryCountsByRepositoryId[repository.id] || 0,
		}));
	}, [localRepositories, localRepositoryEntries]);

	const repositoryListItems: RepositoryListItem[] = useMemo(() => {
		return isRepositoriesSuccess ? remoteRepositories : loadReppositoriesLocal();
	}, [isRepositoriesSuccess, remoteRepositories, localRepositories, localRepositoryEntries]);

	const sortedRepositoryListItems = useMemo(
		() =>
			[...repositoryListItems].sort(
				(a, b) => (a.rank ?? 0) - (b.rank ?? 0),
			),
		[repositoryListItems],
	);

	const [dragItemId, setDragItemId] = useState<string | null>(null);
	const [reorderItemId, setReorderItemId] = useState<string | null>(null);
	const [localOrder, setLocalOrder] = useState<string[] | null>(null);
	const isTouchReorder = Boolean(reorderItemId);
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	const menuAnchorEl = useRef<Element | null>(null);
	const [menuRepository, setMenuRepository] = useState<RepositoryListItem | null>(null);

	const closeListItemMenu = useCallback(() => {
		setMenuRepository(null);
		menuAnchorEl.current = null;
	}, []);

	const openListItemMenu = useCallback((repository: RepositoryListItem, el: Element) => {
		setMenuRepository(repository);
		menuAnchorEl.current = el;
	}, []);

	const rowMenuOptions: RowMenuOption[] = useMemo(
		() => [
			...(isMobile
				? [
						{
							content: RowMenuOptionReorder,
							onClick: RowMenuOptionEffect.Reorder,
							key: 'reorderOption',
						} as RowMenuOption,
				]
				: []),
		],
		[isMobile],
	);

	const entryIds = useMemo(
		() => sortedRepositoryListItems.map((r) => r.id),
		[sortedRepositoryListItems],
	);

	const repoById = useMemo(() => {
		const map = new Map<string, RepositoryListItem>();
		sortedRepositoryListItems.forEach((r) => map.set(r.id, r));
		return map;
	}, [sortedRepositoryListItems]);

	const currentOrder = localOrder ?? entryIds;

	const applyRankUpdates = useCallback(
		async (order: string[]) => {
			const updates = order.map((id, rank) => ({ id, rank }));
			if (userProfile) {
				await Promise.all(
					updates.map(({ id, rank }) => {
						const repo = repoById.get(id);
						if (!repo) return Promise.resolve();
						return updateRepositoryMutation({
							id,
							rank,
							title: repo.title,
							singular: repo.singular ?? '',
						}).unwrap();
					}),
				);
				dispatch(api.util.invalidateTags(['Repositories']));
			} else {
				dispatch(entriesUpdateMany(updates));
			}
		},
		[userProfile, repoById, updateRepositoryMutation, dispatch],
	);

	const commitReorder = useCallback(() => {
		if (!localOrder) return;
		void (async () => {
			await applyRankUpdates(localOrder);
			setLocalOrder(null);
			setReorderItemId(null);
		})();
	}, [localOrder, applyRankUpdates]);

	const commitOrder = useCallback(
		(order: string[]) => {
			void (async () => {
				await applyRankUpdates(order);
				setLocalOrder(null);
				setReorderItemId(null);
			})();
		},
		[applyRankUpdates],
	);

	const handleRowMenuReorderClick = useCallback(
		(item: RowMenuItem) => {
			closeListItemMenu();
			setReorderItemId(item.id);
			setLocalOrder(entryIds);
		},
		[closeListItemMenu, entryIds],
	);

	const handleReorderTargetClick = useCallback(
		(e: React.MouseEvent, targetId: string) => {
			if (!reorderItemId) return;
			if ((e.target as HTMLElement).closest('button')) return;
			if (reorderItemId === targetId) {
				setReorderItemId(null);
				setLocalOrder(null);
				return;
			}
			const order = localOrder ?? entryIds;
			const fromIndex = order.indexOf(reorderItemId);
			const toIndex = order.indexOf(targetId);
			if (fromIndex === -1 || toIndex === -1) return;
			const next = [...order];
			next.splice(fromIndex, 1);
			next.splice(toIndex, 0, reorderItemId);
			commitOrder(next);
		},
		[reorderItemId, localOrder, entryIds, commitOrder],
	);

	const handleMouseUp = useCallback(() => {
		if (!dragItemId) return;
		setDragItemId(null);
		commitReorder();
	}, [dragItemId, commitReorder]);

	useEffect(() => {
		window.addEventListener('mouseup', handleMouseUp);
		return () => {
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [handleMouseUp]);

	const handleRowMouseDown = useCallback(
		(id: string) => {
			setDragItemId(id);
			setLocalOrder(entryIds);
		},
		[entryIds],
	);

	const handleRowMouseEnter = useCallback(
		(id: string) => {
			if (!dragItemId) return;
			if (dragItemId === id) return;
			const order = localOrder ?? entryIds;
			const fromIndex = order.indexOf(dragItemId);
			const toIndex = order.indexOf(id);
			if (fromIndex === -1 || toIndex === -1) return;
			const next = [...order];
			next.splice(fromIndex, 1);
			next.splice(toIndex, 0, dragItemId);
			setLocalOrder(next);
		},
		[dragItemId, localOrder, entryIds],
	);

	const closeBookmarkColorPopover = useCallback(() => {
		setBookmarkColorAnchor(null);
		setBookmarkColorRepository(null);
	}, []);

	const handlePickBookmarkColor = useCallback(
		(hex: string) => {
			if (!bookmarkColorRepository) return;
			const payload = {
				id: bookmarkColorRepository.id,
				title: bookmarkColorRepository.title,
				singular: bookmarkColorRepository.singular ?? '',
				color: hex,
				isBookmarked: true,
			};
			if (userProfile) {
				updateRepositoryMutation(payload);
			} else {
				dispatch(entriesUpdateMany([{ ...payload, type: bookmarkColorRepository.type }]));
			}
			closeBookmarkColorPopover();
		},
		[bookmarkColorRepository, userProfile, updateRepositoryMutation, dispatch, closeBookmarkColorPopover],
	);

	const handleClearBookmark = useCallback(() => {
		if (!bookmarkColorRepository) return;
		const payload = {
			id: bookmarkColorRepository.id,
			title: bookmarkColorRepository.title,
			singular: bookmarkColorRepository.singular ?? '',
			color: null as string | null,
			isBookmarked: false,
		};
		if (userProfile) {
			updateRepositoryMutation(payload);
		} else {
			dispatch(entriesUpdateMany([{ ...payload, type: bookmarkColorRepository.type }]));
		}
		closeBookmarkColorPopover();
	}, [bookmarkColorRepository, userProfile, updateRepositoryMutation, dispatch, closeBookmarkColorPopover]);

	return (
		<Container maxWidth='sm' sx={{ mb: 5 }}>
			<Typography variant='h4' component='h1' sx={{ mt: 2, mb: 3 }}>
				My collections
			</Typography>
			{repositoryListItems.length === 0 ? (
				<Typography variant='body2' color='text.secondary'>
					No collections yet.
				</Typography>
			) : (
				<List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
					{currentOrder.map((id) => {
						const repository = repoById.get(id);
						if (!repository) return null;
						const isReorderHighlight = dragItemId === repository.id || reorderItemId === repository.id;
						return (
							<ListItem
								component={Paper}
								key={repository.id}
								sx={{
									backgroundColor: isReorderHighlight ? 'action.selected' : 'transparent',
									transition: 'background-color 0.2s ease-in-out',
									cursor: isTouchReorder ? 'pointer' : undefined,
									boxShadow: '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)',
									gap: 2,
								}}
								onMouseEnter={() => handleRowMouseEnter(repository.id)}
								onClick={isTouchReorder ? (e) => handleReorderTargetClick(e, repository.id) : undefined}
								secondaryAction={
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
										{!isMobile && (
											<Tooltip title='Drag to reorder' disableInteractive>
												<IconButton
													onMouseDown={(e) => {
														e.stopPropagation();
														handleRowMouseDown(repository.id);
													}}
													aria-label='Reorder collection'
												>
													<DragIndicatorIcon fontSize='small' />
												</IconButton>
											</Tooltip>
										)}
										{isMobile && (
											<IconButton
												onClick={(e) => {
													e.stopPropagation();
													openListItemMenu(repository, e.currentTarget);
												}}
												aria-label='Collection menu'
											>
												<MoreHorizIcon fontSize='small' />
											</IconButton>
										)}
										<Tooltip title='Open configuration' disableInteractive>
											<IconButton
												component={Link}
												to={`/collection/${repository.id}/settings`}
												onClick={(e) => e.stopPropagation()}
											>
												<SettingsIcon fontSize='small' />
											</IconButton>
										</Tooltip>
										<Tooltip title='View entries' disableInteractive>
											<IconButton
												component={Link}
												to={`/collection/${repository.id}`}
												onClick={(e) => e.stopPropagation()}
											>
												<ArrowForwardIosIcon fontSize='small' />
											</IconButton>
										</Tooltip>
									</Box>
								}
							>
								<Tooltip title='Bookmark this collection' disableInteractive>
									<IconButton
										size='small'
										aria-label='Choose bookmark color'
										onClick={(e) => {
											e.stopPropagation();
											setBookmarkColorAnchor(e.currentTarget);
											setBookmarkColorRepository(repository);
										}}
									>
										{repository.color ? (
											<BookmarkIcon sx={{ color: repository.color }} />
										) : (
											<BookmarkBorderIcon />
										)}
									</IconButton>
								</Tooltip>
								<ListItemText
									primary={repository.title}
									secondary={`${repository.entryCount} entries`}
								/>
							</ListItem>
						);
					})}
				</List>
			)}
			<Box display='flex' mt={1}>
				<Button size='medium' color='primary' onClick={() => dispatch(isAddRepositoryDialogOpenSetState(true))} startIcon={<AddIcon />}>
					New collection
				</Button>
			</Box>
			<BookmarkColorPopover
				open={Boolean(bookmarkColorAnchor)}
				anchorEl={bookmarkColorAnchor}
				onClose={closeBookmarkColorPopover}
				onPickColor={handlePickBookmarkColor}
				onClearBookmark={handleClearBookmark}
			/>
			<EntryMenu
				options={rowMenuOptions}
				anchorEl={menuAnchorEl.current}
				onClose={closeListItemMenu}
				item={menuRepository}
				onRowReorderClick={handleRowMenuReorderClick}
			/>
		</Container>
	);
}

export default CollectionsView;
